package gr.tech.GRAttestation.pages.admin.models

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaFilter
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.icons.fa.FaRotateLeft
import gr.tech.GRAttestation.components.PaginationControls
import gr.tech.GRAttestation.components.modals.SKUInputModal
import gr.tech.GRAttestation.components.layouts.AdminPageLayout
import gr.tech.GRAttestation.components.widgets.AlertManager
import gr.tech.GRAttestation.components.widgets.ConfirmModal
import gr.tech.GRAttestation.components.widgets.NavBreadcrumb
import gr.tech.GRAttestation.components.widgets.UserAlert
import gr.tech.GRAttestation.infrastructure.api.adminCreateNewSKU
import gr.tech.GRAttestation.infrastructure.api.adminDeleteSKUByID
import gr.tech.GRAttestation.infrastructure.api.adminFetchAllSKUs
import gr.tech.GRAttestation.infrastructure.api.adminUpdateSKU
import gr.tech.GRAttestation.models.tables.BasicTableFilters
import gr.tech.GRAttestation.sections.tables.AdminSKUsTable
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.util.Constants
import gr.tech.GRAttestation.util.closeModal
import gr.tech.GRAttestation.util.showBootstrapModal
import gr.tech.models.skus.SKU_STATUS
import gr.tech.models.skus.SkuDTO
import gr.tech.models.skus.blankSKUDTO
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Page()
@Composable
fun AdminSKUsPage() {
    AdminPageLayout() { context ->
        AdminSKUsScreen(context)
    }
}

@Composable
fun AdminSKUsScreen(context: PageContext) {
    val scope = rememberCoroutineScope()
    var triggerLaunchedEffect by remember { mutableStateOf(false) }
    val alertManager = remember { AlertManager() }
    alertManager.DisplayAlerts()

    val skus = remember { mutableStateListOf<SkuDTO>() }
    var skuTotalCount by remember { mutableStateOf(0) }
    val pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT
    val totalPages = (skuTotalCount + pageSize - 1) / pageSize
    var currentPage by remember { mutableStateOf(1) }

    // Filter Data
    var basicFilters by remember { mutableStateOf(BasicTableFilters()) }
    var selectedDateColumnLabel by remember { mutableStateOf("Created At") }
    var selectedSkuStatus by remember { mutableStateOf(SKU_STATUS.ACTIVE) }

    // Modal Data
    var currentSKUInformation: SkuDTO by remember { mutableStateOf(blankSKUDTO) }
    var currentSKUInformationError by remember { mutableStateOf("") }
    var toDeleteSKU by remember { mutableStateOf<SkuDTO?>(null) }
    var toRestoreSKU by remember { mutableStateOf<SkuDTO?>(null) }

    LaunchedEffect(Unit, triggerLaunchedEffect, currentPage, basicFilters, selectedSkuStatus) {
        adminFetchAllSKUs(
            pageNumber = currentPage,
            pageSize = pageSize,
            skuStatus = selectedSkuStatus,
            basicFilters = basicFilters,
            onSuccess = { response ->
                if (response.success && response.data != null) {
                    skus.clear()
                    skus.addAll(response.data.items)
                    skuTotalCount = response.data.totalCount
                    if (skus.isEmpty()) { currentPage = 1 }
                }
            },
            onError = {
                console.log("Error with adminFetchAllProjects(): $it")
            }
        )
    }

    // Add New or Edit SKU
    SKUInputModal(
        modalId = "AdminSKUInputModal",
        skuInformation = currentSKUInformation,
        formError = currentSKUInformationError,
        onClose = {
            currentSKUInformation = blankSKUDTO.copy(id = -2)
            closeModal("AdminSKUInputModal")
        },
        onSubmit = {
            scope.launch {
                currentSKUInformation = it
                currentSKUInformationError = " "
                if (it.id > 0) {
                    // This is an edit of a SKU
                    adminUpdateSKU(
                        editedSku = it,
                        onError = {},
                        onSuccess = { apiResponse ->
                            if (apiResponse.success) {
                                triggerLaunchedEffect = !triggerLaunchedEffect
                                alertManager.addAlert(UserAlert(message = "SKU Updated!", status = 2))
                                closeModal("AdminSKUInputModal")
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Updating SKU", status = 0))
                                currentSKUInformationError = apiResponse.error?.message ?: "Error updating."
                            }
                        },
                    )
                } else {
                    // This is a new form
                    adminCreateNewSKU(
                        skuDTO = it,
                        onError = {},
                        onSuccess = { apiResponse ->
                            if (apiResponse.success) {
                                triggerLaunchedEffect = !triggerLaunchedEffect
                                alertManager.addAlert(UserAlert(message = "SKU Added!", status = 2))
                                closeModal("AdminSKUInputModal")
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Adding SKU", status = 0))
                                currentSKUInformationError = apiResponse.error?.message ?: "Error creating."
                            }
                        },
                    )
                }
            }
        },
    )

    // Confirm Deletion Modal
    ConfirmModal(
        modalId = "adminConfirmDelete",
        confirmMessage = "Are you sure you want to delete this SKU?",
        onClose = {
            closeModal("adminConfirmDelete")
        },
        onConfirm = {
            toDeleteSKU?.let {
                scope.launch {
                    adminDeleteSKUByID(
                        skuId = toDeleteSKU!!.id,
                        onSuccess = {
                            if (it.success) {
                                alertManager.addAlert(UserAlert(message = "SKU Deleted!", status = 2))
                                skus.removeAt(skus.indexOf(toDeleteSKU))
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Deleting SKU!", status = 0))
                            }
                        },
                        onError = {
                            alertManager.addAlert(UserAlert(message = "Error Deleting SKU!", status = 0))
                        }
                    )
                    closeModal("adminConfirmDelete")
                }
            }
        }
    )

    // Confirm Restore Modal
    ConfirmModal(
        modalId = "adminConfirmRestore",
        confirmMessage = "Are you sure you want to restore this SKU?",
        onClose = {
            closeModal("adminConfirmRestore")
        },
        onConfirm = {
            toRestoreSKU?.let {
                scope.launch {
                    adminDeleteSKUByID(
                        skuId = toRestoreSKU!!.id,
                        restore = true,
                        onSuccess = {
                            if (it.success) {
                                alertManager.addAlert(UserAlert(message = "SKU Restored!", status = 2))
                                skus.removeAt(skus.indexOf(toRestoreSKU))
                            } else {
                                alertManager.addAlert(UserAlert(message = "Error Restoring SKU!", status = 0))
                            }
                        },
                        onError = {
                            alertManager.addAlert(UserAlert(message = "Error Restoring SKU!", status = 0))
                        }
                    )
                    closeModal("adminConfirmRestore")
                }
            }
        }
    )

    Column(
        modifier = Modifier
            .padding(24.px)
            .backgroundColor(Theme.HalfWhite.rgb)
            .fillMaxSize()
    ) {
        NavBreadcrumb(ctx = context)

        // Top Row Buttons
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        )
        {
            Button(
                attrs = Modifier
                    .classNames("btn", "btn-primary")
                    .onClick {
                        scope.launch {
                            adminFetchAllSKUs(
                                pageNumber = currentPage,
                                pageSize = pageSize,
                                skuStatus = selectedSkuStatus,
                                basicFilters = basicFilters,
                                onSuccess = { response ->
                                    if (response.success && response.data != null) {
                                        skus.clear()
                                        skus.addAll(response.data.items)
                                        skuTotalCount = response.data.totalCount
                                    }
                                },
                                onError = {
                                    console.log(it)
                                },
                            )
                        }
                    }
                    .margin(right = 16.px)
                    .toAttrs()
            ) {
                FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                Text("Refresh")
            }

            Button(
                attrs = Modifier.classNames("btn", "btn-primary")
                    .margin(right = 16.px)
                    .onClick {}
                    .toAttrs{
                        attr("data-bs-toggle", "collapse")
                        attr("data-bs-target", "#adminSKUFilters")
                    }
            ) {
                FaFilter(modifier = Modifier.margin(right = 8.px))
                Text("Filters")
            }

            Button(
                attrs = Modifier.classNames("btn", "btn-primary")
                    .margin(right = 16.px)
                    .onClick {
                        currentSKUInformation = blankSKUDTO.copy(id = -3)
                        showBootstrapModal("AdminSKUInputModal")
                    }
                    .toAttrs{}
            ) {
                FaPlus(modifier = Modifier.margin(right = 8.px))
                Text("New")
            }

            Spacer()

            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { pageNumber ->
                    currentPage = pageNumber

                }
            )
        }

        // Filters Section
        Column(
            modifier = Modifier.id("adminSKUFilters").classNames("collapse")
                .margin(top = 8.px, bottom = 16.px)
                .padding(8.px)
                .backgroundColor(Colors.WhiteSmoke)
                .borderRadius(8.px)
                .fillMaxWidth()
        )
        {
            // Your Filters Row
            Row(
                modifier = Modifier
                    .fillMaxWidth().gap(16.px),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {

                // Dropdown for column
                Div(attrs = Modifier.classNames("dropdown").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-secondary", "dropdown-toggle")
                            .id("dateColumnDropdown")
                            .toAttrs {
                                attr("data-bs-toggle", "dropdown")
                            }
                    ) {
                        Text(selectedDateColumnLabel)
                    }
                    Ul(attrs = Modifier.classNames("dropdown-menu").toAttrs()) {
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item")
                                    .onClick {
                                        basicFilters = basicFilters.copy(
                                            dateColumn = "createdAt"
                                        )
                                        selectedDateColumnLabel = "Created At"
                                }.toAttrs()
                            ) {
                                Text("Created At")
                            }
                        }
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item")
                                    .onClick {
                                        basicFilters = basicFilters.copy(
                                            dateColumn = "updatedAt"
                                        )
                                        selectedDateColumnLabel = "Updated At"
                                    }
                                    .toAttrs()
                            ) {
                                Text("Updated At")
                            }
                        }
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item").onClick {
                                    basicFilters = basicFilters.copy(
                                        dateColumn = "expiresAt"
                                    )
                                    selectedDateColumnLabel = "Expires At"
                                }.toAttrs()
                            ) {
                                Text("Expires At")
                            }
                        }
                    }
                }

                // ASCENDING / DESCENDING toggle
                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick {
                                basicFilters = basicFilters.copy(
                                    isAscending = true
                                )
                            }
                            .toAttrs()
                    ) {
                        Text("ASCENDING")
                    }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isAscending) "btn-primary" else "btn-outline-primary")
                            .onClick {
                                basicFilters = basicFilters.copy(
                                    isAscending = false
                                )
                            }
                            .toAttrs()
                    ) {
                        Text("DESCENDING")
                    }
                }

                // ASCENDING / DESCENDING toggle
                Div(attrs = Modifier.classNames("btn-group").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { basicFilters = basicFilters.copy(isBefore = true) }
                            .toAttrs()
                    ) {
                        Text("BEFORE")
                    }
                    Button(
                        attrs = Modifier
                            .classNames("btn", if (!basicFilters.isBefore) "btn-primary" else "btn-outline-primary")
                            .onClick { basicFilters = basicFilters.copy(isBefore = false) }
                            .toAttrs()
                    ) {
                        Text("AFTER")
                    }
                }

                // Date input
                Input(
                    type = InputType.Date,
                    attrs = Modifier
                        .classNames("form-control")
                        .width(200.px)
                        .toAttrs {
                            attr("value", basicFilters.dateString)
                            onChange {
                                console.log(it.value)
                                basicFilters = basicFilters.copy(
                                    dateString = it.value
                                )
                            }
                        }
                )

                Spacer()

                // Reset Filters button
                Button(
                    attrs = Modifier.classNames("btn", "btn-secondary")
                        .onClick {
                            basicFilters = BasicTableFilters()
                            selectedDateColumnLabel = "Created At"
                        }
                        .toAttrs{}
                ) {
                    FaRotateLeft(modifier = Modifier.margin(right = 8.px))
                    Text("Reset")
                }
            }

            Row(
                modifier = Modifier
                    .margin(top = 16.px)
                    .fillMaxWidth().gap(16.px),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Div(attrs = Modifier.classNames("dropdown").toAttrs()) {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-secondary", "dropdown-toggle")
                            .id("dateColumnDropdown")
                            .toAttrs {
                                attr("data-bs-toggle", "dropdown")
                            }
                    ) {
                        Text(selectedSkuStatus.name)
                    }
                    Ul(attrs = Modifier.classNames("dropdown-menu").toAttrs()) {
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item")
                                    .onClick {
                                        selectedSkuStatus = SKU_STATUS.ACTIVE
                                    }
                                    .toAttrs()
                            ) {
                                Text("Active")
                            }
                        }
                        Li {
                            A(
                                href = "#",
                                attrs = Modifier.classNames("dropdown-item")
                                    .onClick {
                                        selectedSkuStatus = SKU_STATUS.DELETED
                                    }
                                    .toAttrs()
                            ) {
                                Text("Deleted")
                            }
                        }
                    }
                }
            }
        }

        AdminSKUsTable(
            skus = skus,
            onView = { sku -> },
            onEdit = { sku ->
                currentSKUInformation = sku
                showBootstrapModal("AdminSKUInputModal")
            },
            onDelete = { sku ->
                toDeleteSKU = sku
                showBootstrapModal("adminConfirmDelete")
            },
            onRestore = { sku ->
                toRestoreSKU = sku
                showBootstrapModal("adminConfirmRestore")
            }
        )

        Row(
            modifier = Modifier.fillMaxWidth().margin(top = 16.px),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            PaginationControls(
                currentPage = currentPage,
                totalPages = totalPages,
                onPageSelected = { pageNumber ->
                    currentPage = pageNumber

                }
            )
        }

    }
}